import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// ─── Constants ────────────────────────────────────────────────────────────────

const ZERO_DECIMAL_CURRENCIES = new Set([
  'bif','clp','djf','gnf','jpy','kmf','krw',
  'mga','pyg','rwf','ugx','vnd','vuv','xaf','xof','xpf',
]);

const CURRENCY_SYMBOLS: Record<string, string> = {
  usd:'$', eur:'€', gbp:'£', inr:'₹', jpy:'¥', cad:'CA$', aud:'A$', chf:'CHF',
};

const SPORT_ACCENTS = [
  { border: '#BFDBFE', bg: '#EFF6FF', badge: '#DBEAFE', badgeText: '#1D4ED8' },
  { border: '#BBF7D0', bg: '#F0FDF4', badge: '#DCFCE7', badgeText: '#15803D' },
  { border: '#FDE68A', bg: '#FFFBEB', badge: '#FEF3C7', badgeText: '#92400E' },
  { border: '#E9D5FF', bg: '#FAF5FF', badge: '#EDE9FE', badgeText: '#6D28D9' },
  { border: '#FBCFE8', bg: '#FDF2F8', badge: '#FCE7F3', badgeText: '#9D174D' },
];

// ─── Types ────────────────────────────────────────────────────────────────────

interface LineItem {
  description: string;
  amountMinor: number;
  type: 'base' | 'addon';
}

interface SportGroup {
  code: string;
  label: string;
  items: LineItem[];
  baseAmountMinor: number;
  addonSubtotalMinor: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatStripeAmount(amountMinor: number, currency: string): string {
  const c = currency.toLowerCase();
  if (!Number.isFinite(amountMinor)) return '-';
  const isZero = ZERO_DECIMAL_CURRENCIES.has(c);
  return (isZero ? amountMinor : amountMinor / 100).toFixed(isZero ? 0 : 2);
}

function getCurrencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency.toLowerCase()] ?? currency.toUpperCase();
}

function toMinorUnits(raw: number): number {
  if (!Number.isFinite(raw)) return 0;
  const isLikelyMajor = !Number.isInteger(raw) || Math.abs(raw) < 500;
  return isLikelyMajor ? Math.round(raw * 100) : raw;
}

function cleanDescription(code: string, desc: string): string {
  const prefix = `${code} `;
  return desc.startsWith(prefix) ? desc.slice(prefix.length) : desc;
}

/** Extract a 4-digit year from a string, e.g. "Season 2023" → "2023" */
function extractYear(desc: string): string {
  const match = desc.match(/\b(19|20)\d{2}\b/);
  return match ? match[0] : desc;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function InvoiceStatus() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);

  const raw: any = (location.state as any)?.invoiceResponseData ?? null;
  const subscriptionDetails: any[] = (location.state as any)?.subscriptionDetails ?? [];

  useEffect(() => {
    // Small delay to ensure navigation state is available
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  // ── Invoice header ────────────────────────────────────────────────────────
  const invoice = useMemo(() => {
    const d = raw?.data ?? raw;
    if (!d || typeof d !== 'object') return null;
    const dueSec  = Number(d.due_date);
    const dueDate = Number.isFinite(dueSec) ? new Date(dueSec * 1000) : null;
    return {
      currency    : (d.currency           as string) || 'usd',
      amountDue   : (d.amount_due         as number) ?? null,
      dueDateLabel: dueDate?.toLocaleDateString(undefined, { year:'numeric', month:'long', day:'numeric' }) ?? null,
      hostedUrl   : (d.hosted_invoice_url as string) || null,
      pdfUrl      : (d.invoice_pdf        as string) || null,
    };
  }, [raw]);

  // ── Group by sport ────────────────────────────────────────────────────────
  const sportGroups = useMemo((): SportGroup[] => {
    if (!subscriptionDetails.length) return [];
    const map = new Map<string, SportGroup>();

    subscriptionDetails.forEach((sub: any) => {
      const code        = (sub.sports_code || sub.sport_code || 'OTHER').toString().trim().toUpperCase();
      const type        = sub.type === 'addon' ? 'addon' : 'base';
      const desc        = (sub.description || '').toString().trim();
      const amountMinor = toMinorUnits(Number(sub.amount));

      if (!map.has(code)) {
        map.set(code, { code, label: code, items: [], baseAmountMinor: 0, addonSubtotalMinor: 0 });
      }
      const group     = map.get(code)!;
      const cleanDesc = cleanDescription(code, desc) || desc;

      if (type === 'base' && cleanDesc) {
        group.label           = cleanDesc;
        group.baseAmountMinor = amountMinor;
      }
      if (type === 'addon') {
        group.addonSubtotalMinor += amountMinor;
      }
      group.items.push({ description: cleanDesc, amountMinor, type });
    });

    return Array.from(map.values());
  }, [subscriptionDetails]);

  const currency = invoice?.currency ?? 'usd';
  const symbol   = getCurrencySymbol(currency);
  const showCode = !['$','€','£','₹','¥'].includes(symbol);
  const amount   = invoice?.amountDue != null
    ? formatStripeAmount(invoice.amountDue, currency)
    : null;

  // ── Actions ───────────────────────────────────────────────────────────────
  const handleDownload = () => {
    const url = raw?.invoice_pdf ?? raw?.data?.invoice_pdf;
    if (url) {
      const a = Object.assign(document.createElement('a'), {
        href: String(url), target: '_blank', rel: 'noreferrer', download: 'invoice.pdf',
      });
      document.body.appendChild(a); a.click(); a.remove();
    }
    navigate('/dashboard');
  };

  const handlePayNow = () => {
    const url = raw?.hosted_invoice_url ?? raw?.data?.hosted_invoice_url;
    if (url) window.open(String(url), '_blank', 'noreferrer');
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div
        className="min-h-screen w-full flex items-center justify-center bg-slate-50 px-4 py-10"
        style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif" }}
      >
        <div className="flex flex-col items-center gap-4">
          <span className="inline-block w-10 h-10 border-4 border-slate-200 border-t-red-500 rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading invoice...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center bg-slate-50 px-4 py-10 relative overflow-hidden"
      style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif" }}
    >
      {/* bg blobs */}
      <div className="pointer-events-none absolute -top-28 -right-28 w-96 h-96 rounded-full"
           style={{ background: 'radial-gradient(circle,rgba(239,68,68,.07) 0%,transparent 70%)' }} />
      <div className="pointer-events-none absolute -bottom-24 -left-20 w-80 h-80 rounded-full"
           style={{ background: 'radial-gradient(circle,rgba(99,102,241,.05) 0%,transparent 70%)' }} />

      {/* Card */}
      <div
        className="relative z-10 w-full max-w-lg bg-white rounded-2xl border border-slate-100 px-10 py-10 flex flex-col items-center"
        style={{ boxShadow: '0 4px 32px rgba(0,0,0,.08),0 1px 4px rgba(0,0,0,.04)' }}
      >
        {/* ── Header ── */}
        <div className="flex flex-col items-center gap-3 pb-6 w-full text-center">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg,#22c55e,#16a34a)', boxShadow: '0 4px 16px rgba(34,197,94,.3)' }}
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
              <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Invoice Generated</h1>
          <p className="text-sm text-slate-500 leading-relaxed max-w-xs">
            Your subscription is in a <strong className="text-slate-700">grace period</strong>.
            Settle the invoice to restore full access.
          </p>
        </div>

        <div className="w-full h-px bg-slate-100" />

        {invoice ? (
          <>
            {/* ── Amount Due ── */}
            {amount !== null && (
              <div className="flex flex-col items-center gap-1 pt-6 pb-1 w-full">
                <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">Amount Due</span>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-light text-slate-400 leading-none">{symbol}</span>
                  <span
                    className="font-extrabold text-slate-900 leading-none"
                    style={{ fontSize: '60px', letterSpacing: '-2px' }}
                  >
                    {amount}
                  </span>
                  {showCode && (
                    <span className="text-lg font-medium text-slate-400 self-end pb-1.5 ml-1">
                      {currency.toUpperCase()}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* ── Due Date ── */}
            {invoice.dueDateLabel && (
              <div className="flex items-center justify-center gap-2 w-full mt-3 mb-5 px-4 py-2.5 rounded-xl bg-amber-50 border border-amber-200">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="shrink-0">
                  <rect x="3" y="4" width="18" height="18" rx="3" stroke="#92400e" strokeWidth="1.8"/>
                  <path d="M16 2v4M8 2v4M3 10h18" stroke="#92400e" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
                <span className="text-sm text-amber-700">Due by</span>
                <span className="text-sm font-bold text-amber-900">{invoice.dueDateLabel}</span>
              </div>
            )}

            {/* ── Sport Groups ── */}
            {sportGroups.length > 0 && (
              <div className="w-full flex flex-col gap-3 mb-2">
                <span className="text-xs font-bold tracking-widest uppercase text-slate-400 text-center">
                  Selected Sports &amp; Add-ons
                </span>

                {sportGroups.map((sport, si) => {
                  const accent = SPORT_ACCENTS[si % SPORT_ACCENTS.length];
                  const addons = sport.items.filter(i => i.type === 'addon');

                  // Extract years from addon descriptions and join as "2022, 2023, 2024"
                  const addonYears = addons
                    .map(item => extractYear(item.description))
                    .filter(Boolean)
                    .join(', ');

                  return (
                    <div
                      key={sport.code}
                      className="w-full rounded-xl border overflow-hidden"
                      style={{ borderColor: accent.border }}
                    >
                      {/* ── Sport name row ── */}
                      <div
                        className="flex items-center justify-between px-4 py-3"
                        style={{ background: accent.bg }}
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          {/* code badge */}
                          <span
                            className="shrink-0 text-xs font-bold px-2 py-0.5 rounded-md"
                            style={{ background: accent.badge, color: accent.badgeText }}
                          >
                            {sport.code}
                          </span>
                          {/* purchased pill */}
                          <span className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200">
                            <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                              <path d="M2 6l3 3 5-5" stroke="#15803D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Purchased
                          </span>
                        </div>
                        {/* base subscription price */}
                        <span className="shrink-0 text-sm font-bold text-slate-800 tabular-nums ml-3">
                          {symbol}{formatStripeAmount(sport.baseAmountMinor, currency)}
                        </span>
                      </div>

                      {/* ── Divider ── */}
                      <div style={{ height: '1px', background: accent.border }} />

                      {/* ── Add-ons body ── */}
                      <div className="bg-white px-4 py-2.5">
                        {addons.length > 0 ? (
                          <>
                            <p className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-1.5">
                              Add-on
                            </p>
                            <div className="flex items-baseline justify-between gap-3">
                              <span className="text-sm text-slate-600">{addonYears}</span>
                              <span className="shrink-0 text-sm font-semibold text-slate-700 tabular-nums">
                                {symbol}{formatStripeAmount(sport.addonSubtotalMinor, currency)}
                              </span>
                            </div>
                          </>
                        ) : (
                          <span className="text-xs text-slate-400 italic">Base subscription only — no add-ons</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center gap-2 w-full my-6 px-4 py-3.5 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="shrink-0">
              <circle cx="12" cy="12" r="9" stroke="#92400e" strokeWidth="1.8"/>
              <path d="M12 8v4M12 16h.01" stroke="#92400e" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Invoice details unavailable. You can still go to the dashboard.
          </div>
        )}

        <div className="w-full h-px bg-slate-100 mt-3" />

        {/* ── Actions ── */}
        <div className="grid grid-cols-2 gap-2.5 w-full pt-5">
          <button
            type="button"
            onClick={handleDownload}
            className="col-span-2 py-3.5 rounded-xl text-sm font-bold text-white tracking-wide"
            style={{ background: 'linear-gradient(135deg,#EF4444,#DC2626)', boxShadow: '0 4px 14px rgba(239,68,68,.35)' }}
          >
            Download Invoice
          </button>
          <button
            type="button"
            onClick={handlePayNow}
            disabled={!invoice?.hostedUrl}
            className="py-3.5 rounded-xl text-sm font-semibold text-slate-800 bg-white border border-slate-300 hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Pay Now
          </button>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="py-3.5 rounded-xl text-sm font-semibold text-slate-500 bg-slate-50 border border-slate-200 hover:bg-slate-100 transition-colors"
          >
            Dashboard →
          </button>
        </div>
      </div>
    </div>
  );
}